/* eslint-disable tailwindcss/no-custom-classname */
/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';

import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { CmsImage } from '../CmsImage/CmsImage';
import { MaterialIcon } from '../MaterialIcon';
import { ProductGridCardActions } from '../ProductGridCard/components/ProductGridCardActions';
import { tagColor as getTagColor } from '../Tag';
import Tooltip from '../Tooltip';
import type { RewardGridCardProps } from './types';

export type { RewardGridCardProps } from './types';
export {
  getRewardGridCardProductPreviewItems,
  getRewardGridCardValueText,
} from './helpers';

const MAX_VISIBLE_PREVIEW_ITEMS = 4;

const RewardGridCard: React.FC<RewardGridCardProps> = ({
  id,
  className,
  tagColor,
  tagText,
  imageUrl,
  imageAlt = 'Reward image',
  title,
  rewardValueLabel,
  rewardValueText,
  rewardValueTooltip,
  previewItems = [],
  previewImageAlt = 'Eligible product preview',
  primaryCtaLabel,
  primaryCtaUrl,
  onPrimaryCtaClick,
  secondaryCtaLabel,
  secondaryCtaUrl,
  onSecondaryCtaClick,
}) => {
  const [isSmallViewport, setIsSmallViewport] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    const mediaQuery = window.matchMedia('(max-width: 360px)');
    const handleChange = (event: MediaQueryListEvent | MediaQueryList) => {
      setIsSmallViewport(event.matches);
    };

    handleChange(mediaQuery);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const maxVisiblePreviewItems = isSmallViewport
    ? 3
    : MAX_VISIBLE_PREVIEW_ITEMS;
  const previewItemWidthClass = isSmallViewport
    ? 'w-[calc((100%-8px)/3)]'
    : 'w-[calc((100%-12px)/4)]';
  const previewItemSizes = isSmallViewport
    ? 'calc((100vw-8px)/3)'
    : 'calc((100vw-12px)/4)';
  const previewSlotsCount = Math.min(
    previewItems.length,
    maxVisiblePreviewItems,
  );
  const shouldShowCounter = previewItems.length > maxVisiblePreviewItems;
  const visiblePreviewCount = shouldShowCounter
    ? Math.max(previewSlotsCount - 1, 1)
    : previewSlotsCount;
  const visiblePreviewItems = previewItems.slice(0, visiblePreviewCount);
  const hiddenPreviewCount = Math.max(
    previewItems.length - visiblePreviewItems.length,
    0,
  );

  return (
    <article
      className={classNames(
        'flex h-full w-40 overflow-hidden rounded-[10px] border border-borders/default bg-fill/card md:w-58 md:rounded-radius/card lg:w-68',
        className,
      )}
      data-reward-id={id}
    >
      <div className="flex size-full flex-col gap-2 p-2 md:gap-[10px] md:p-[10px] lg:gap-spacing/gap/soft-12px lg:p-spacing/padding/soft-12px">
        {tagText && (
          <div
            className={classNames(
              'flex w-full items-center rounded-radius/tooltip px-spacing/padding/soft-8px py-spacing/padding/soft-4px',
              getTagColor(tagColor?.toLowerCase()),
            )}
          >
            <p className="truncate text-left mhg-alpha-caption" title={tagText}>
              {tagText}
            </p>
          </div>
        )}

        {imageUrl && (
          <div className="overflow-hidden rounded-radius/button border border-borders/default bg-gradient-to-b from-fill/card to-fill/alternative-1 p-spacing/padding/soft-8px">
            <CmsImage
              src={imageUrl}
              alt={imageAlt}
              width={533}
              height={342}
              sizes={{ _: '100vw', md: 272 }}
              className="h-[90px] w-full object-contain md:h-auto md:aspect-[533/342]"
            />
          </div>
        )}

        {(title || rewardValueText) && (
          <div className="flex flex-col gap-spacing/gap/soft-6px">
            {title && (
              <h3 className="line-clamp-2 text-text/primary mhg-alpha-typography-title-2">
                {title}
              </h3>
            )}

            {rewardValueText && (
              <div className="flex w-fit items-center gap-1 rounded-radius/tooltip bg-fill/primary-light px-spacing/padding/soft-8px py-spacing/padding/soft-4px">
                <p className="text-fill/primary-dark mhg-alpha-typography-table-cell-strong">
                  {rewardValueLabel
                    ? `${rewardValueLabel} ${rewardValueText}`
                    : rewardValueText}
                </p>
                {rewardValueTooltip && (
                  <Tooltip
                    content={rewardValueTooltip}
                    onOpenEvent="click"
                    tooltipClass="max-w-[300px]"
                  >
                    <MaterialIcon
                      icon="info"
                      px={18}
                      className="cursor-pointer text-fill/primary-dark opacity-50 !text-[16px] md:!text-[18px]"
                      aria-label="Additional info"
                    />
                  </Tooltip>
                )}
              </div>
            )}
          </div>
        )}

        <div className="mt-auto">
          {previewItems.length > 0 && (
            <div className="flex items-center pb-2 gap-spacing/gap/soft-8px md:pb-[10px] lg:pb-spacing/gap/soft-12px">
              {visiblePreviewItems.map((previewItem, index) => (
                <div
                  key={`${previewItem.imageUrl ?? 'no-image'}-${previewItem.title ?? index}`}
                  className={classNames(
                    'aspect-[78/50] shrink-0 overflow-hidden rounded-[4px] border border-borders/default md:w-[calc((100%-24px)/4)]',
                    previewItemWidthClass,
                  )}
                  title={previewItem.title}
                >
                  {previewItem.imageUrl ? (
                    <CmsImage
                      src={previewItem.imageUrl}
                      alt={previewItem.title || previewImageAlt}
                      width={78}
                      height={50}
                      sizes={{ _: previewItemSizes, md: 78 }}
                      className="size-full object-cover"
                    />
                  ) : (
                    <div className="flex size-full items-center justify-center bg-fill/card px-1 text-center text-[8px] leading-[1.1] text-text/secondary-lighter md:text-[9px]">
                      {previewItem.title ?? previewImageAlt}
                    </div>
                  )}
                </div>
              ))}

              {hiddenPreviewCount > 0 && (
                <div
                  className={classNames(
                    'flex aspect-[78/50] shrink-0 items-center justify-center rounded-[4px] border border-borders/default bg-fill/card text-text/secondary-lighter mhg-alpha-bullet-2 md:w-[calc((100%-24px)/4)] md:mhg-alpha-body-2',
                    previewItemWidthClass,
                  )}
                >
                  +{hiddenPreviewCount}
                </div>
              )}
            </div>
          )}

          <ProductGridCardActions
            primaryCtaLabel={primaryCtaLabel}
            primaryCtaUrl={primaryCtaUrl}
            onPrimaryCtaClick={onPrimaryCtaClick}
            primaryCtaPosition="footer"
            primaryCtaIcon={isSmallViewport ? 'none' : 'arrow'}
            secondaryCtaLabel={secondaryCtaLabel}
            secondaryCtaUrl={secondaryCtaUrl}
            onSecondaryCtaClick={onSecondaryCtaClick}
            placement="footer"
          />
        </div>
      </div>
    </article>
  );
};

export default RewardGridCard;
